import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LoadZone from "@ucs/Components/LoadZone";
import ChevronRight from "@ucs/Icons/ChevronRight";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    link = "link",
    linkLabel = "linkLabel",
    bgColor = "bgColor",
    tileCount = "tileCount",
    orderByDate = "orderByDate",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.link]: LinkFieldValue;
        [fields.linkLabel]: string;
        [fields.bgColor]: string;
        [fields.tileCount]: "two" | "three" | "more";
        [fields.orderByDate]: boolean;
    };
}

type Props = OwnProps;

export interface ContentTilesViewStyles {
    wrapperTwo?: InjectableCss;
    wrapperThree?: InjectableCss;
    wrapperMore?: InjectableCss;
    topSection?: InjectableCss;
    tileSectionTwo?: InjectableCss;
    tileSectionThree?: InjectableCss;
    tileSectionMore?: InjectableCss;
    heading?: TypographyProps;
    link?: LinkProps;
    buttons?: InjectableCss;
}

export const contentTilesViewStyles: ContentTilesViewStyles = {
    wrapperTwo: {
        css: css`
            width: fit-content;
            margin: 0 auto;
            padding: 96px 50px 86px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding: 40px 20px;
                    `,
                    css`
                        width: 100%;
                        padding: 48px 24px;
                    `,
                    css`
                        padding: 62px 30px 54px;
                    `,
                    css`
                        padding: 72px 40px 66px;
                    `,
                ]);
            }}
        `,
    },
    wrapperThree: {
        css: css`
            width: 100%;
            margin: 0 auto;
            padding: 96px 50px 86px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 20px;
                    `,
                    css`
                        padding: 48px 24px;
                    `,
                    css`
                        padding: 62px 30px 54px;
                    `,
                    css`
                        padding: 72px 40px 66px;
                    `,
                    css`
                        width: fit-content;
                    `,
                ]);
            }}
            @media only screen and (min-width: 800px) and (max-width: 900px) {
                width: fit-content;
            }
        `,
    },
    wrapperMore: {
        css: css`
            width: 100%;
            padding: 44px 40px 48px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 24px 20px;
                    `,
                    css`
                        padding: 30px 24px;
                    `,
                ]);
            }}
        `,
    },
    topSection: {
        css: css`
            width: 100%;
            max-width: 1134px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 20px;
            margin: 0 auto;
            padding-bottom: 48px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 0;
                        flex-direction: column;
                        align-items: center;
                        align-items: flex-start;
                        padding-bottom: 12px;
                    `,
                    css`
                        padding-bottom: 16px;
                    `,
                    css`
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
    tileSectionTwo: {
        css: css`
            width: 100%;
            & > div {
                width: fit-content;
                grid-gap: 30px;
                display: grid;
                grid-template-columns: repeat(2, minmax(350px, 360px));
                justify-items: center;
                @media only screen and (min-width: 0px) and (max-width: 550px) {
                    width: 100% !important;
                    grid-gap: 10px;
                    grid-template-columns: repeat(1, minmax(0, 1fr));
                }
                @media only screen and (min-width: 550px) and (max-width: 700px) {
                    width: 100% !important;
                    grid-gap: 20px;
                    grid-template-columns: repeat(2, minmax(0, 360px));
                }
                @media only screen and (min-width: 700px) and (max-width: 830px) {
                    grid-template-columns: repeat(2, minmax(300px, 360px));
                }
            }
        `,
    },
    tileSectionThree: {
        css: css`
            width: 100%;
            & > div {
                width: 100%;
                grid-gap: 30px;
                display: grid;
                grid-template-columns: repeat(3, minmax(350px, 1fr));
                justify-items: center;
                @media only screen and (min-width: 0px) and (max-width: 550px) {
                    grid-gap: 10px;
                    grid-template-columns: repeat(1, minmax(0, 1fr));
                }
                @media only screen and (min-width: 550px) and (max-width: 767px) {
                    grid-gap: 20px;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                @media only screen and (min-width: 767px) and (max-width: 800px) {
                    grid-gap: 30px;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                @media only screen and (min-width: 800px) and (max-width: 900px) {
                    width: fit-content;
                    grid-template-columns: repeat(2, minmax(0, 350px));
                }
                @media only screen and (min-width: 900px) and (max-width: 1100px) {
                    grid-gap: 20px;
                    grid-template-columns: repeat(3, minmax(0, 350px));
                }
                @media only screen and (min-width: 1100px) {
                    width: fit-content;
                    grid-template-columns: repeat(3, minmax(0, 360px));
                }
            }
        `,
    },
    tileSectionMore: {
        css: css`
            width: 100%;
            & > div {
                width: fit-content;
                margin: 0 auto;
                display: grid;
                grid-gap: 30px;
                grid-template-rows: auto;
                justify-items: center;
                @media only screen and (min-width: 0px) and (max-width: 550px) {
                    width: 100% !important;
                    grid-gap: 10px;
                    grid-template-columns: repeat(1, minmax(0, 1fr));
                }
                @media only screen and (min-width: 550px) and (max-width: 610px) {
                    width: 100% !important;
                    grid-gap: 20px;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                @media only screen and (min-width: 610px) and (max-width: 840px) {
                    grid-gap: 30px;
                    grid-template-columns: repeat(2, minmax(0, 350px));
                }
                @media only screen and (min-width: 840px) and (max-width: 910px) {
                    grid-gap: 20px;
                    grid-template-columns: repeat(3, minmax(0, 350px));
                }
                @media only screen and (min-width: 910px) and (max-width: 1280px) {
                    grid-template-columns: repeat(3, minmax(0, 350px));
                }
                @media (min-width: 1280px) and (max-width: 1760px) {
                    grid-template-columns: repeat(4, minmax(0, 360px));
                }
                @media (min-width: 1760px) and (max-width: 2180px) {
                    grid-template-columns: repeat(5, minmax(0, 360px));
                }
                @media (min-width: 2180px) {
                    grid-template-columns: repeat(6, minmax(0, 360px));
                }
            }
        `,
    },
    heading: {
        variant: "h2",
    },
    link: {
        typographyProps: {
            size: 18,
        },
        icon: {
            iconProps: {
                size: 16,
                color: "primary",
                css: css`
                    margin: 10px 8px 10px 0;
                `,
                src: ChevronRight,
            },
            position: "left",
        },
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 10px;
        `,
    },
};

const styles = contentTilesViewStyles;

const ContentTilesView: React.FC<Props> = ({ id, fields }) => {
    const { title, linkLabel, tileCount, orderByDate } = fields;
    const link = useGetLink(fields.link);
    const wrapperStyles =
        tileCount === "more" ? styles.wrapperMore : tileCount === "two" ? styles.wrapperTwo : styles.wrapperThree;
    const tileSectionStyles =
        tileCount === "more"
            ? styles.tileSectionMore
            : tileCount === "two"
            ? styles.tileSectionTwo
            : styles.tileSectionThree;

    const handleBackToTop = () => {
        const view = document.getElementById("view-top");
        if (view) {
            window.scrollTo(0, view.offsetTop);
        }
    };

    return (
        <StyledWrapper {...wrapperStyles} id="view-top">
            {(title || link.url) && tileCount !== "more" && (
                <StyledWrapper {...styles.topSection}>
                    <Typography {...styles.heading}>{title}</Typography>
                    {link.url && (
                        <Link href={link.url} {...styles.link}>
                            {linkLabel}
                        </Link>
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper {...tileSectionStyles}>
                <LoadZone contentId={id} zoneName="Content" orderByDate={orderByDate} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentTilesView,
    definition: {
        group: "Common",
        displayName: "Content Tile View",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.linkLabel,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#E5E5E5",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.tileCount,
                displayName: "Number of tiles",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Two", value: "two" },
                    { displayName: "Three", value: "three" },
                    { displayName: "More", value: "more" },
                ],
                defaultValue: "two",
                isRequired: true,
            },
            {
                name: fields.orderByDate,
                displayName: "Order by date",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
